/**
 * The Remembered Search store: the only code that touches browser storage for
 * the remembered-search feature. A consent check, if one is ever needed, goes
 * here and nowhere else.
 *
 * Each locale's key holds a list of entries, newest first, capped at
 * `MAX_REMEMBERED_SEARCHES`. An entry is offered only within the configured
 * lifetime of its commit time — checked on every read against the *current*
 * setting, so lowering it applies to entries already stored — and is deleted
 * once lapsed. Every failure (storage unavailable, quota exceeded, a value
 * that is not ours) is silent and reads as nothing: this module never throws
 * into the search component.
 */

/** The visitor's choice of where to go, as chosen; or "any". */
export type RememberedDestination =
  | 'any'
  | Array<{ id: number; name: string; isHotel: boolean }>;

/** The visitor's choice of where to fly from, as chosen; or "any". */
export type RememberedDeparture =
  | 'any'
  | Array<{ code: string; name: string; isGroup: boolean }>;

/** The duration bucket the visitor chose; or "any". */
export type RememberedDuration = 'any' | { value: number; name: string };

/**
 * The departure dates the visitor chose, in the shape they chose them: a whole
 * month (`YYYY-MM`), or a date (`YYYY-MM-DD`) with its flexibility in days
 * (null for an exact date); or "any". `label` is how the date button shows the
 * month or date, without any flexibility wording.
 */
export type RememberedDates =
  | 'any'
  | { kind: 'month'; month: string; label: string }
  | { kind: 'date'; date: string; flexibility: number | null; label: string };

export interface RememberedRoom {
  adults: number;
  childAges: number[];
}

/** What a commit records. Search options authored on the page never are. */
export interface RememberedCriteria {
  destination: RememberedDestination;
  departure: RememberedDeparture;
  duration: RememberedDuration;
  dates: RememberedDates;
  rooms: RememberedRoom[];
}

export interface RememberedSearch extends RememberedCriteria {
  version: typeof SHAPE_VERSION;
  /** Epoch milliseconds. */
  committedAt: number;
}

/**
 * Bumped whenever the stored shape changes, so an entry written by an older
 * bundle is recognised and ignored rather than half-read. 2: dates added — a
 * version-1 entry has no date criterion, and reading its absence as "any"
 * would make a missing criterion indistinguishable from a corrupt one.
 */
const SHAPE_VERSION = 2;

/**
 * How many Remembered Searches each locale keeps, newest first. One for now:
 * a later "recent searches" list needs only this raised. The stored value is
 * a list either way; an entry carries its own `version`, so the list itself
 * has none.
 */
export const MAX_REMEMBERED_SEARCHES = 1;

const DEFAULT_LIFETIME_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const keyFor = (locale: string): string =>
  `sundeals.rememberedSearch.${locale.toLowerCase()}`;

/**
 * The lifetime, in days. A build-time setting: esbuild inlines
 * `process.env.REMEMBERED_SEARCH_LIFETIME_DAYS` into the bundle the same way it
 * inlines `SITE_URL`, so changing it needs only a bundle rebuild.
 */
const lifetimeMs = (): number => {
  const days = Number(process.env.REMEMBERED_SEARCH_LIFETIME_DAYS);
  return (Number.isFinite(days) && days > 0 ? days : DEFAULT_LIFETIME_DAYS) * DAY_MS;
};

/** `localStorage`, or null where reaching it throws (blocked, sandboxed). */
const storage = (): Storage | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

const isNamedList = (
  value: unknown,
  idKey: 'id' | 'code',
  idType: 'number' | 'string',
  flag: 'isHotel' | 'isGroup'
): boolean =>
  Array.isArray(value) &&
  value.every(
    item =>
      item !== null &&
      typeof item === 'object' &&
      typeof item[idKey] === idType &&
      typeof item.name === 'string' &&
      typeof item[flag] === 'boolean'
  );

const isRememberedDates = (value: unknown): value is RememberedDates => {
  if (value === 'any') return true;
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, any>;
  if (typeof v.label !== 'string') return false;
  if (v.kind === 'month') {
    return typeof v.month === 'string' && /^\d{4}-\d{2}$/.test(v.month);
  }
  return (
    v.kind === 'date' &&
    typeof v.date === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(v.date) &&
    (v.flexibility === null ||
      (Number.isInteger(v.flexibility) && v.flexibility > 0))
  );
};

const isRememberedSearch = (value: unknown): value is RememberedSearch => {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, any>;
  return (
    v.version === SHAPE_VERSION &&
    typeof v.committedAt === 'number' &&
    (v.destination === 'any' || isNamedList(v.destination, 'id', 'number', 'isHotel')) &&
    (v.departure === 'any' || isNamedList(v.departure, 'code', 'string', 'isGroup')) &&
    (v.duration === 'any' ||
      (v.duration !== null &&
        typeof v.duration === 'object' &&
        typeof v.duration.value === 'number' &&
        typeof v.duration.name === 'string')) &&
    isRememberedDates(v.dates) &&
    Array.isArray(v.rooms) &&
    v.rooms.every(
      (room: any) =>
        room !== null &&
        typeof room === 'object' &&
        typeof room.adults === 'number' &&
        Array.isArray(room.childAges) &&
        room.childAges.every((age: unknown) => typeof age === 'number')
    )
  );
};

/** Store `list` as the value for `locale`, or remove the key when it is empty. */
const storeList = (locale: string, list: unknown[]): void => {
  if (list.length === 0) {
    forgetRememberedSearch(locale);
    return;
  }
  storage()?.setItem(keyFor(locale), JSON.stringify(list));
};

/** Remember `criteria` as the latest search committed in `locale`. */
export const writeRememberedSearch = (
  locale: string,
  criteria: RememberedCriteria
): void => {
  const entry: RememberedSearch = {
    ...criteria,
    version: SHAPE_VERSION,
    committedAt: Date.now(),
  };
  try {
    const earlier = readRememberedSearches(locale, { forgetLapsed: false });
    storeList(locale, [entry, ...earlier].slice(0, MAX_REMEMBERED_SEARCHES));
  } catch {
    // Quota exceeded, or storage refused: nothing is remembered.
  }
};

/**
 * The Remembered Searches for `locale`, newest first; empty when there are
 * none to offer. A stored value that is not a list reads as nothing; an entry
 * in it that is not ours is skipped, and a lapsed one is dropped — and, unless
 * `forgetLapsed` is false, removed from storage (the key with it, once nothing
 * is left).
 *
 * `forgetLapsed: false` reads without writing anything. The early script
 * inlined into the page uses it: it is built without the bundle's lifetime
 * setting, so only the bundle may decide an entry is gone.
 */
export const readRememberedSearches = (
  locale: string,
  { forgetLapsed = true }: { forgetLapsed?: boolean } = {}
): RememberedSearch[] => {
  try {
    const raw = storage()?.getItem(keyFor(locale));
    if (!raw) return [];
    const stored: unknown = JSON.parse(raw);
    if (!Array.isArray(stored)) return [];
    const now = Date.now();
    const isLapsed = (item: unknown): boolean =>
      isRememberedSearch(item) && now - item.committedAt >= lifetimeMs();
    const kept = stored.filter(item => !isLapsed(item));
    // Entries this bundle cannot read are left in place, not judged lapsed.
    if (forgetLapsed && kept.length < stored.length) storeList(locale, kept);
    return kept.filter(isRememberedSearch);
  } catch {
    return [];
  }
};

/**
 * The Remembered Search for `locale` — the most recent one — or null when
 * there is none to offer. Options as for `readRememberedSearches`.
 */
export const readRememberedSearch = (
  locale: string,
  options: { forgetLapsed?: boolean } = {}
): RememberedSearch | null =>
  readRememberedSearches(locale, options)[0] ?? null;

/** Forget the Remembered Search for `locale`. */
export const forgetRememberedSearch = (locale: string): void => {
  try {
    storage()?.removeItem(keyFor(locale));
  } catch {
    // Nothing to forget, or storage refused.
  }
};
